/**
 * Tabs
 * ======================================
 * - toggle class on tab nav item
 * - mobile: animated height on .info-tabs__navitem-text
 * - desktop: opacity + translateY transition on .js-tabs-list items
 * - html markup required (js-tabs-select, js-tabs-nav, js-tabs-list)
 * - support for mobile select
 * - support multiple widgets
 */

const WIDGET = '.js-tabs'
const ACTIVECLASS = 'current'
const MOBILE_BREAKPOINT = 768
const DURATION = 350

class Tabs {
  constructor() {
    this.elements = document.querySelectorAll(WIDGET)

    this.elements.forEach((widget) => {
      const nav = widget.querySelectorAll('.js-tabs-nav > *')
      const select = widget.querySelector('.js-tabs-select')

      // Init desktop tab transitions
      widget.querySelectorAll('.js-tabs-list > *').forEach((tab) => {
        tab.style.transition = `opacity ${DURATION}ms ease, transform ${DURATION}ms ease`
        if (tab.hasAttribute('hidden')) {
          tab.style.opacity = '0'
          tab.style.transform = 'translateY(8px)'
        } else {
          tab.style.opacity = '1'
          tab.style.transform = 'none'
        }
      })

      // Init mobile animated height
      nav.forEach((navItem) => {
        const content = navItem.querySelector('.info-tabs__navitem-text')
        if (!content) return
        content.style.overflow = 'hidden'
        content.style.transition = `height ${DURATION}ms ease`
        content.style.height = navItem.classList.contains(ACTIVECLASS)
          ? content.scrollHeight + 'px'
          : '0'
      })

      nav.forEach((el) => el.addEventListener('click', this.clickHandler))
      if (select) select.addEventListener('change', this.selectHandler)
    })
  }

  clickHandler = (e) => {
    const button = e.currentTarget
    const widget = button.closest(WIDGET)
    this.switchTab(widget, this.getIndex(button), button)
  }

  selectHandler = (e) => {
    const select = e.currentTarget
    const widget = select.closest(WIDGET)
    this.switchTab(widget, select.selectedIndex)
  }

  switchTab = (widget, index, clickedElement = null) => {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT
    isMobile
      ? this.switchMobile(widget, index, clickedElement)
      : this.switchDesktop(widget, index)
    window.dispatchEvent(new CustomEvent('scroll'))
  }

  switchMobile = (widget, index, clickedElement) => {
    widget.querySelectorAll('.js-tabs-nav > *').forEach((navItem, i) => {
      const content = navItem.querySelector('.info-tabs__navitem-text')
      const isTarget = i === index

      navItem.classList.toggle(ACTIVECLASS, isTarget)

      if (!content) return

      if (isTarget) {
        content.style.height = content.scrollHeight + 'px'
        content.addEventListener('transitionend', () => {
          content.style.height = 'auto'
        }, { once: true })
      } else {
        if (content.style.height === 'auto') {
          content.style.height = content.scrollHeight + 'px'
          content.getBoundingClientRect() // force reflow before collapsing
        }
        requestAnimationFrame(() => {
          content.style.height = '0'
        })
      }
    })

    if (clickedElement) {
      const top = clickedElement.getBoundingClientRect().top + window.pageYOffset - 120
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  switchDesktop = (widget, index) => {
    const tabs = widget.querySelectorAll('.js-tabs-list > *')
    const nav = widget.querySelectorAll('.js-tabs-nav > *')
    const tabsList = widget.querySelector('.js-tabs-list')

    nav.forEach((navItem, i) => navItem.classList.toggle(ACTIVECLASS, i === index))

    // Immediately hide all non-target tabs so layout doesn't stack
    tabs.forEach((tab, i) => {
      clearTimeout(tab._hideTimer)
      if (i !== index) {
        tab.setAttribute('hidden', 'hidden')
        tab.style.opacity = '0'
        tab.style.transform = 'translateY(8px)'
      }
    })

    // Animate in the target tab
    const target = tabs[index]
    if (target) {
      target.style.transition = 'none'
      target.style.opacity = '0'
      target.style.transform = 'translateY(8px)'
      target.removeAttribute('hidden')
      // Double rAF ensures initial state is painted before transition starts
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          target.style.transition = `opacity ${DURATION}ms ease, transform ${DURATION}ms ease`
          target.style.opacity = '1'
          target.style.transform = 'none'
        })
      })
    }

    if (tabsList) {
      const top = tabsList.getBoundingClientRect().top + window.pageYOffset - 150
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  getIndex = (el) => [...el.parentNode.children].indexOf(el)
}

new Tabs()
